// eslint-disable-next-line import/no-extraneous-dependencies
import { MaterialCommunityIcons } from '@expo/vector-icons'
import {
  Attachment,
  AttachmentFile,
  Conversation,
  Emoji,
  Id,
  Member,
  Mention,
  Message,
  MessageAction,
  Resource,
  User,
} from '@nextforce/api'
import { useHeaderHeight } from '@react-navigation/elements'
import { Icon, Text, makeStyles, useTheme } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { BUBBLE_AVATAR_SIZE } from 'chat/shared/constants'
import { getMessageGroupData, handleTextChanged } from 'chat/shared/util'
import { Image } from 'expo-image'
import { isNil } from 'lodash'
import React, {
  JSX,
  RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  ActivityIndicator,
  FlatListProps,
  Keyboard,
  Modal,
  Platform,
  Pressable,
  Animated as RNAnimated,
  View,
  ViewStyle,
  useWindowDimensions,
} from 'react-native'
import { GestureHandlerRootView, Swipeable } from 'react-native-gesture-handler'
import {
  AvatarProps,
  Bubble,
  BubbleProps,
  IMessage as ChatMessage,
  Day,
  GiftedChat,
  Message as GiftedChatMessage,
  IMessage,
  LeftRightStyle,
  MessageImageProps,
  MessageProps,
  MessageTextProps,
  Time,
} from 'react-native-gifted-chat'
import { AnimatedList } from 'react-native-gifted-chat/src/MessagesContainer'
import ParsedText from 'react-native-parsed-text'
import { Menu, MenuProvider } from 'react-native-popup-menu'
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated'
import { ReanimatedScrollEvent } from 'react-native-reanimated/lib/typescript/hook/commonTypes'
import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context'
import {} from 'react-native-web'
import ActionSheetMenu from 'shared/components/chat/action_sheet_menu'
import { ATTACHMENT_PREVIEW_SIZE } from 'shared/components/chat/attachment_preview'
import { NonSelectableText } from 'shared/components/chat/non_selectable_text'
import { DragDropZone } from 'shared/components/drag_drop_zone'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import {
  UserAvatar,
  getBackgroundColorById,
} from 'shared/components/user/user_avatar'
import { MAX_MESSAGE_LENGTH, WEB_SCREEN_WIDTH } from 'shared/constants'
import {
  getDarkShadeColor,
  getLightShadeColor,
  stripInvalidCodepoints,
} from 'shared/utils'
import { isPdfAttachment } from '../file/screen'
import { extractUrl } from '../queries/shared/utils'
import { scrollToIndexWithRetry } from '../util'
import {
  MESSAGE_ACCESSORY_BAR_HEIGHT,
  MessageAccessoryBar,
} from './accessory_bar'
import {
  COMPOSER_HEIGHT,
  CustomComposer,
  CustomInputToolbar,
  CustomSendButton,
} from './input_toolbar'
import { LinkPreviewComponent } from './link_preview'
import { PinnedButton } from './pinActionButton'
import Reactions from './reaction'
import { MessageReplyBubble } from './reply'
import { REPLY_MESSAGE_BAR_HEIGHT } from './reply_message_bar'
import { ShareResourceMessageView } from './share_resource'
import {
  THUMBNAIL_IMAGE_MARGIN_HORIZONTAL,
  THUMBNAIL_IMAGE_WIDTH,
  ThumbnailImage,
} from './thumbnail_image'

const MIN_ITEMS_COUNT = 12

const convertMessageToChatMessage = (msg: Message): ChatMessage => ({
  _id: msg.id,
  text: msg.body || '',
  createdAt: msg.createdAt,
  user: {
    _id: msg.user.id,
    name: msg.user.id.toString(),
    avatar: undefined,
  },
})

interface BubbleMessagesProps {
  getAllowedActions?: (
    message: Message,
    isPinnedMessage?: boolean
  ) => MessageAction[] | undefined
  user: User | undefined
  conversation: Conversation
  online: boolean
  prefillMessageText: string | undefined
  messageBody: string
  selectedAttachments: AttachmentFile[]
  chatMessages: ChatMessage[]
  pinnedChatMessages: ChatMessage[]
  messageIndex: number | undefined
  isUploading: boolean
  readByCounts?: {
    [messageId: string]: number
  }
  members: readonly Member[] | undefined | null
  setChatMessageBody: (text: string) => void
  onSendMessage(): Promise<void>
  onShowFile(attachment: Attachment, isPinnedMessage?: boolean): void
  onShowReactors(messageId: string, emoji: Emoji): void
  onHideDescription(): void
  onSendReaction(id: string, emoji: Emoji): void
  onClearScrolling(): void
  onShowMultiAttachments: (messageId: string, attachmentIndex: number) => void
  pasteAttachments(chatGroupId: string, files: AttachmentFile[]): void
  onLongPress: (context: any, message?: Message) => void
  onMessageAction?: (message: Message, action: MessageAction) => void
  getMessage(id?: Id): Message | undefined
  getPinnedMessage(id?: Id): Message | undefined
  getMessageGroup: (id?: Id) => Message[] | undefined
  getPinnedMessageGroup: (id?: Id) => Message[] | undefined
  onLinkPress: (url: string) => void
  onPressAcknowledgeResource: (resource: Resource) => void
  showPinned: boolean
  setShowPinned: React.Dispatch<React.SetStateAction<boolean>>
  readOnly: boolean
  replyMessage: Message | undefined
  setReplyMessage: React.Dispatch<React.SetStateAction<Message | undefined>>
  swipeableRef: RefObject<Swipeable | null>
  shouldFocusInput: boolean
  setShouldFocusInput: React.Dispatch<React.SetStateAction<boolean>>
  firstUnreadMessageId: Id | undefined
  unreadMessageCount: number | undefined
  firstUnreadMessageIndex: number | undefined
  onClearScrollToFirstUnreadMessage: () => void
  isLoadingEarlier: boolean
  hasMoreMessages: boolean | undefined
  onLoadEarlier: () => void
}

export function BubbleMessagesView({
  conversation,
  messageBody,
  chatMessages,
  pinnedChatMessages,
  messageIndex,
  isUploading,
  onLongPress,
  onClearScrolling,
  onHideDescription,
  onSendMessage,
  onSendReaction,
  onShowFile,
  onShowMultiAttachments,
  onShowReactors,
  online,
  readByCounts,
  members,
  pasteAttachments,
  prefillMessageText,
  onMessageAction,
  selectedAttachments,
  setChatMessageBody,
  user,
  getMessage,
  getPinnedMessage,
  getMessageGroup,
  getPinnedMessageGroup,
  onLinkPress,
  onPressAcknowledgeResource,
  showPinned,
  setShowPinned,
  readOnly,
  replyMessage,
  setReplyMessage,
  shouldFocusInput,
  setShouldFocusInput,
  firstUnreadMessageId,
  unreadMessageCount,
  firstUnreadMessageIndex,
  onClearScrollToFirstUnreadMessage,
  getAllowedActions,
  isLoadingEarlier,
  hasMoreMessages,
  onLoadEarlier,
}: BubbleMessagesProps) {
  const { theme } = useTheme()
  const styles = useStyles()
  const swipeableRef = useRef<Swipeable | null>(null)
  const characterLimitAlertShown = useRef(false)
  const [descriptionBoxHeight, setDescriptionBoxHeight] = useState(0)

  const handleInputTextChanged = (text: string) => {
    // Call the shared function that shows the alert when necessary
    handleTextChanged(text, setChatMessageBody, characterLimitAlertShown)
  }

  const isSendDisabled =
    messageBody.length >= MAX_MESSAGE_LENGTH ||
    isUploading ||
    (messageBody.trim().length === 0 && selectedAttachments.length === 0)

  const canPostFile = online && conversation?.postsAllowed === 'messageOrFile'
  const messagesBottomPadding = getMessagesBottomPadding(
    selectedAttachments.length > 0,
    !!replyMessage
  )
  selectedAttachments.length > 0
    ? replyMessage
      ? ATTACHMENT_PREVIEW_SIZE + COMPOSER_HEIGHT + REPLY_MESSAGE_BAR_HEIGHT
      : ATTACHMENT_PREVIEW_SIZE + COMPOSER_HEIGHT
    : 0

  const description = useMemo(() => {
    if (
      !conversation ||
      conversation.descriptionHidden ||
      !conversation.description ||
      conversation.description.trim().length === 0
    ) {
      return null
    }

    return conversation.description
  }, [conversation])

  const pinnedMenuRef = useRef<Menu | null>(null)

  const [selectedPinnedMessage, setSelectedPinnedMessage] = useState<
    Message | undefined
  >()

  const onPinnedChatMessageLongPress = useCallback(
    (context: any, chatMessage?: ChatMessage) => {
      if (!chatMessage) {
        return
      }
      const message = getPinnedMessage(chatMessage._id.toString())
      if (!message) {
        return
      }
      setSelectedPinnedMessage(message)
      if (pinnedMenuRef.current) {
        pinnedMenuRef.current.open().catch(() => undefined)
      }
      Keyboard.dismiss()
    },
    [getPinnedMessage]
  )

  const onChatMessageLongPress = useCallback(
    (context: any, chatMessage?: ChatMessage) => {
      if (!chatMessage) return
      const message = getMessage(chatMessage._id.toString())
      if (!message) return
      if (showPinned) {
        onPinnedChatMessageLongPress(
          context,
          convertMessageToChatMessage(message)
        )
      } else {
        onLongPress(context, message)
      }
      Keyboard.dismiss()
    },
    [getMessage, onLongPress, onPinnedChatMessageLongPress, showPinned]
  )

  const onBubblePress = useCallback(
    (message?: Message, isPinnedMessage?: boolean) => {
      if (!message) {
        return
      }
      const attachment = message.attachment

      if (attachment) {
        setShowPinned(false)
        onShowFile(attachment, isPinnedMessage)
      }
    },
    [onShowFile, setShowPinned]
  )

  const renderBubble = useCallback(
    (props: BubbleProps<ChatMessage>, isPinnedMessage = false) => {
      const getCurrentMessageGroup = isPinnedMessage
        ? getPinnedMessageGroup
        : getMessageGroup
      const messageGroup = getCurrentMessageGroup(
        props.currentMessage?._id.toString()
      )

      if (!props.currentMessage || !messageGroup) {
        return null
      }

      const chatMessage: ChatMessage = props.currentMessage

      const readByCount =
        readByCounts && readByCounts[chatMessage._id]
          ? readByCounts[chatMessage._id]
          : undefined

      let isPreviousGroupHasReactions = false
      if (props.previousMessage?._id) {
        const previousMessageGroup = getCurrentMessageGroup(
          props.previousMessage?._id.toString()
        )
        isPreviousGroupHasReactions =
          (previousMessageGroup &&
            getMessageGroupData(previousMessageGroup).reactions.length > 0) ??
          false
      }

      const bubbleProps = props

      return (
        <MessageBubble
          conversation={conversation}
          bubbleProps={bubbleProps}
          message={getMessage(props.currentMessage?._id.toString())}
          messageGroup={messageGroup}
          readByCount={readByCount}
          members={members}
          isPreviousBubbleHasReactions={isPreviousGroupHasReactions}
          onReactMessageAction={onSendReaction}
          onShowReactors={onShowReactors}
          onBubblePress={attachment => onShowFile(attachment, isPinnedMessage)}
          userColor={getBackgroundColorById(
            chatMessage.user._id as Id,
            theme.mode
          )}
          showNames={true}
          onLongPress={(context, msg) => {
            if (!msg) return
            if (showPinned) {
              onPinnedChatMessageLongPress(
                context,
                convertMessageToChatMessage(msg)
              )
            } else {
              onLongPress(context, msg)
            }
            Keyboard.dismiss()
          }}
          onLinkPress={onLinkPress}
        />
      )
    },
    [
      getPinnedMessageGroup,
      getMessageGroup,
      readByCounts,
      conversation,
      getMessage,
      members,
      onSendReaction,
      onShowReactors,
      theme.mode,
      onLinkPress,
      onShowFile,
      onLongPress,
      onPinnedChatMessageLongPress,
      showPinned,
    ]
  )

  const renderTime = useCallback(
    (props: any) => {
      return (
        <Time
          {...props}
          timeTextStyle={{
            left: styles.otherTimeText,
            right: styles.selfTimeText,
          }}
        />
      )
    },
    [styles.otherTimeText, styles.selfTimeText]
  )

  const renderDay = useCallback(
    (props: any) => {
      return (
        <Day
          {...props}
          textProps={{ style: styles.dayText }}
          textStyle={styles.dayText}
          wrapperStyle={{ backgroundColor: theme.colors.white }}
        />
      )
    },
    [styles.dayText, theme.colors.white]
  )

  const renderPinnedBubble = useCallback(
    (props: any) => {
      return renderBubble(props, true)
    },
    [renderBubble]
  )

  const renderAccessoryButtons = useCallback(() => {
    return (
      <View>
        <MessageAccessoryBar
          buttonNames={['photo', 'camera', 'upload']}
          chatGroupId={conversation?.id}
        />
      </View>
    )
  }, [conversation?.id])

  const renderPinnedMessageImage = useCallback(
    (props: MessageImageProps<IMessage>) => {
      const { currentMessage: chatMessage } = props

      if (!chatMessage?.image) {
        return null
      }
      const id = chatMessage._id as Id
      const message = getPinnedMessage(id)
      if (message) {
        const messageAttachment = message.attachment
        if (messageAttachment) {
          return (
            <BubbleMessageImages
              isUserSender={props.currentMessage?.user._id === user?.id}
              attachments={[messageAttachment]}
              onShowMultiAttachments={() => onShowMultiAttachments(id, 3)}
              onAttachmentPress={() => onBubblePress(message, true)}
              onLongPressAttachment={() =>
                onPinnedChatMessageLongPress(
                  null,
                  convertMessageToChatMessage(message)
                )
              }
            />
          )
        }
      }
    },
    [
      getPinnedMessage,
      onBubblePress,
      onPinnedChatMessageLongPress,
      onShowMultiAttachments,
      user?.id,
    ]
  )

  const getCurrentMessageFromAttachment = useCallback(
    (id: string | undefined, attachment: Attachment) => {
      const messageGroup = getMessageGroup(id)
      if (messageGroup) {
        const message = messageGroup.find(
          m => m.attachment?.uri === attachment.uri
        )
        return message
      }
    },
    [getMessageGroup]
  )

  const renderMessageImage = useCallback(
    (props: MessageImageProps<IMessage>) => {
      const { currentMessage: chatMessage } = props
      const message = getMessage(chatMessage?._id as Id)
      if (!chatMessage?.image) {
        return null
      }
      const id = chatMessage._id as Id
      const messageGroup = getMessageGroup(id)
      const isUserSender = props.currentMessage?.user._id === user?.id
      const { attachments } = getMessageGroupData(messageGroup)

      if (attachments && attachments.length > 0) {
        return (
          <View>
            <View style={{ width: THUMBNAIL_IMAGE_WIDTH }}>
              <MessageReplyBubble
                isSender={false}
                replyMessage={message?.replyToMessage}
              />
            </View>

            <BubbleMessageImages
              attachments={attachments}
              isUserSender={isUserSender}
              onShowMultiAttachments={() => onShowMultiAttachments(id, 3)}
              onAttachmentPress={attachment =>
                onBubblePress(
                  getCurrentMessageFromAttachment(
                    props.currentMessage?._id.toString(),
                    attachment
                  )
                )
              }
              onLongPressAttachment={attachment =>
                onLongPress(
                  null,
                  getCurrentMessageFromAttachment(
                    props.currentMessage?._id.toString(),
                    attachment
                  )
                )
              }
            />
          </View>
        )
      } else {
        const firstMessage = messageGroup?.[0]
        const isShareMessage =
          firstMessage?.subType === 'shareFile' ||
          firstMessage?.subType === 'shareFolder'
        if (isShareMessage) {
          return (
            <ShareResourceMessageView
              message={firstMessage}
              position={isUserSender ? 'right' : 'left'}
              onLongPress={() => onLongPress(null, firstMessage)}
              onPressAcknowledgeResource={onPressAcknowledgeResource}
            />
          )
        }
      }
    },
    [
      getCurrentMessageFromAttachment,
      getMessage,
      getMessageGroup,
      onBubblePress,
      onLongPress,
      onPressAcknowledgeResource,
      onShowMultiAttachments,
      user?.id,
    ]
  )

  const renderAvatar = useCallback(
    (props: AvatarProps<IMessage>) => {
      const { currentMessage: chatMessage } = props

      if (!chatMessage) {
        return null
      }

      return (
        <UserAvatar
          user={getMessage(chatMessage._id as Id)?.user}
          showAvatarIfNoPhone={true}
          size={BUBBLE_AVATAR_SIZE}
        />
      )
    },
    [getMessage]
  )

  const renderLeftAction = useCallback(
    (progressAnimatedValue: RNAnimated.AnimatedValue) => {
      const scale = progressAnimatedValue.interpolate({
        inputRange: [0, 0.5, 1],
        outputRange: [0.5, 0.8, 1],
        extrapolate: 'clamp',
      })

      const translateX = progressAnimatedValue.interpolate({
        inputRange: [0, 1],
        outputRange: [-30, 0],
        extrapolate: 'clamp',
      })

      const opacity = progressAnimatedValue.interpolate({
        inputRange: [0, 0.2, 1],
        outputRange: [0, 0.5, 1],
      })

      return (
        <RNAnimated.View
          style={[
            styles.replyIconContainer,
            { transform: [{ scale }, { translateX }] },
            { opacity },
          ]}
        >
          <View>
            <MaterialCommunityIcons
              name="reply-circle"
              size={30}
              color={'#6E6E73'}
            />
          </View>
        </RNAnimated.View>
      )
    },
    [styles.replyIconContainer]
  )

  const renderMessage = useCallback(
    ({ ...props }: {} & MessageProps<IMessage>) => {
      const message = getMessage(props.currentMessage?._id.toString())
      const messageGroup = getMessageGroup(message?.id)
      const { attachments } = getMessageGroupData(messageGroup)
      const disabled =
        message?.isDeleted ||
        (attachments && attachments.length > 1) ||
        readOnly

      const onSwipeOpenAction = (
        direction: 'left' | 'right',
        swipeable: Swipeable
      ) => {
        swipeableRef.current = swipeable

        if (props.currentMessage) {
          setReplyMessage(getMessage(props.currentMessage?._id as Id))
          swipeableRef.current.close()
          swipeableRef.current = null
        }
      }

      const showUnreadMessageIndicator =
        !!firstUnreadMessageId && firstUnreadMessageId === message?.id

      const isWeb = Platform.OS === 'web'
      return (
        <>
          {showUnreadMessageIndicator && (
            <UnreadMessageIndicator count={unreadMessageCount} />
          )}
          {isWeb ? (
            <GiftedChatMessage {...props} />
          ) : (
            <GestureHandlerRootView>
              <Swipeable
                enabled={!disabled}
                friction={1}
                leftThreshold={5}
                renderLeftActions={renderLeftAction}
                renderRightActions={() => null}
                onSwipeableOpen={(direction, swipeable) =>
                  onSwipeOpenAction(direction, swipeable)
                }
              >
                <GiftedChatMessage {...props} />
              </Swipeable>
            </GestureHandlerRootView>
          )}
        </>
      )
    },
    [
      firstUnreadMessageId,
      getMessage,
      getMessageGroup,
      readOnly,
      renderLeftAction,
      setReplyMessage,
      unreadMessageCount,
    ]
  )

  const ref = useRef<AnimatedList<IMessage>>(null)
  const [isUnreadIndicatorVisible, setIsUnreadIndicatorVisible] = useState(true)
  const [showScrollButton, setShowScrollButton] = useState(false)
  const [isScrollingToUnread, setIsScrollingToUnread] = useState(false)
  const [
    unreadButtonDismissedForThisSession,
    setUnreadButtonDismissedForThisSession,
  ] = useState(false)

  // Effect to reset the dismissal state when the first unread message ID changes,
  // ensuring the button can reappear for new unread sessions.
  useEffect(() => {
    setUnreadButtonDismissedForThisSession(false)
  }, [firstUnreadMessageId])

  const onPressSendMessage = useCallback(async () => {
    await onSendMessage()
    // Clear unread messages indicator when sending a message
    if (firstUnreadMessageId) {
      onClearScrollToFirstUnreadMessage()
    }
    ref.current?.scrollToIndex({
      index: 0,
      animated: false,
    })
  }, [onSendMessage, firstUnreadMessageId, onClearScrollToFirstUnreadMessage])

  // Effect to show the scroll button when there are unread messages,
  // the unread indicator is not visible, and the button hasn't been dismissed for this session.
  useEffect(() => {
    if (
      firstUnreadMessageId &&
      !isUnreadIndicatorVisible &&
      unreadMessageCount &&
      unreadMessageCount > 0 &&
      !unreadButtonDismissedForThisSession && // Check dismissal state
      !isScrollingToUnread // Don't show button while scrolling
    ) {
      setShowScrollButton(true)
    } else {
      setShowScrollButton(false)
    }
  }, [
    firstUnreadMessageId,
    isUnreadIndicatorVisible,
    unreadMessageCount,
    unreadButtonDismissedForThisSession,
    isScrollingToUnread,
  ])

  // Function to manually trigger scroll to unread message
  const scrollToUnreadMessage = useCallback(() => {
    if (firstUnreadMessageIndex !== undefined && firstUnreadMessageIndex >= 0) {
      setIsScrollingToUnread(true)

      scrollToIndexWithRetry(
        ref,
        firstUnreadMessageIndex,
        () => {
          // Called when scroll completes
          setIsScrollingToUnread(false)
          setShowScrollButton(false)
          setUnreadButtonDismissedForThisSession(true)
        },
        {
          onScrollStart: () => {
            setIsScrollingToUnread(true)
          },
        }
      ).catch(console.error)
    }
  }, [firstUnreadMessageIndex])

  const handleDismissScrollToUnread = useCallback(() => {
    setShowScrollButton(false)
    setUnreadButtonDismissedForThisSession(true)
  }, [])

  const firstUnreadMessageIdRef = useRef(firstUnreadMessageId)

  // Keep ref in sync with prop
  useEffect(() => {
    firstUnreadMessageIdRef.current = firstUnreadMessageId
  }, [firstUnreadMessageId])

  const onViewableItemsChanged = useCallback(({ viewableItems }) => {
    const currentFirstUnreadId = firstUnreadMessageIdRef.current
    if (currentFirstUnreadId) {
      const unreadMessageIsViewable = viewableItems.some(
        item => item.item?._id === currentFirstUnreadId && item.isViewable
      )
      setIsUnreadIndicatorVisible(unreadMessageIsViewable)

      if (unreadMessageIsViewable) {
        setShowScrollButton(false)
        setUnreadButtonDismissedForThisSession(true)
      }
    }
  }, [])

  const giftedChatListViewProps:
    | Partial<
        Omit<FlatListProps<ChatMessage>, 'onScroll'> & {
          onScroll?: (event: ReanimatedScrollEvent) => void
        }
      >
    | undefined = useMemo(
    () => ({
      scrollIndicatorInsets: { right: 1 },
      onScrollToIndexFailed: (error: {
        averageItemLength: number
        index: number
      }) => {
        const retryTimerId = setTimeout(() => {
          ref.current?.scrollToIndex({
            index: error.index,
            viewPosition: 0.5,
            animated: true,
          })
        }, 100)
        onClearScrolling()
        return () => clearTimeout(retryTimerId)
      },
      onViewableItemsChanged,
      viewabilityConfig: {
        itemVisiblePercentThreshold: 50,
      },
    }),
    [onClearScrolling, onViewableItemsChanged]
  )

  const dimensions = useWindowDimensions()
  const headerHeight = useHeaderHeight()
  const keyboardVerticalOffset = headerHeight + descriptionBoxHeight

  useEffect(() => {
    if (!description) {
      setDescriptionBoxHeight(0)
    }
  }, [description])

  return (
    <DragDropZone
      style={[styles.container]}
      onDrop={files => pasteAttachments(conversation.id, files)}
      onDropUrl={url => setChatMessageBody(messageBody + ' ' + url)}
    >
      <View style={styles.pinnedButtonContainer}>
        {description ? (
          <View
            style={styles.descriptionBox}
            onLayout={event => {
              setDescriptionBoxHeight(event.nativeEvent.layout.height)
            }}
          >
            <Text style={styles.descriptionText}>
              {stripInvalidCodepoints(description)}
            </Text>
            <PressableOpacity onPress={onHideDescription}>
              <Text style={styles.descriptionCloseButton}>x</Text>
            </PressableOpacity>
            {!showPinned &&
              pinnedChatMessages?.length !== 0 &&
              conversation.type !== 'dm' && (
                <PinnedButton
                  setShowPinned={setShowPinned}
                  descriptionHidden={!description}
                />
              )}
          </View>
        ) : (
          !showPinned &&
          pinnedChatMessages?.length !== 0 &&
          conversation.type !== 'dm' && (
            <PinnedButton
              setShowPinned={setShowPinned}
              descriptionHidden={!description}
            />
          )
        )}
      </View>

      {showPinned && user ? (
        <Modal
          visible={showPinned}
          animationType="slide"
          onRequestClose={() => setShowPinned(false)}
        >
          <SafeAreaProvider>
            <SafeAreaView style={styles.pinnedMessagesModal}>
              <MenuProvider skipInstanceCheck>
                <ActionSheetMenu
                  menuRef={pinnedMenuRef}
                  actions={
                    selectedPinnedMessage && getAllowedActions
                      ? getAllowedActions(selectedPinnedMessage, true)
                      : undefined
                  }
                  onAction={action => {
                    if (selectedPinnedMessage && onMessageAction) {
                      onMessageAction(selectedPinnedMessage, action)
                      setSelectedPinnedMessage(undefined)
                    }
                  }}
                  onClose={() => setSelectedPinnedMessage(undefined)}
                />
                <View style={[styles.pinnedMessagesModalContainer]}>
                  <View style={styles.pinnedMessagesHeader}>
                    <Text
                      style={styles.pinnedMessagesHeaderText}
                    >{`${stripInvalidCodepoints(conversation.name)} : Pinned`}</Text>
                    <Icon
                      name="times"
                      type="font-awesome-5"
                      onPress={() => setShowPinned(false)}
                      color={theme.colors?.primary}
                      solid
                    />
                  </View>
                  <GiftedChat
                    messages={pinnedChatMessages}
                    messagesContainerStyle={styles.pinnedChatMessages}
                    user={{
                      _id: user.id,
                      name: `${user.firstName} ${user.lastName}`,
                      avatar: user.photoUrl ?? undefined,
                    }}
                    renderBubble={renderPinnedBubble}
                    renderMessageImage={renderPinnedMessageImage}
                    renderTime={renderTime}
                    renderDay={renderDay}
                    renderUsername={() => null}
                    renderAccessory={renderAccessoryButtons}
                    renderAvatar={renderAvatar}
                    isAlignedTop={false}
                    timeTextStyle={{
                      left: { color: theme.colors?.black },
                      right: { color: theme.colors?.black },
                    }}
                    renderInputToolbar={() => <></>}
                    onLongPressMessage={onPinnedChatMessageLongPress}
                    renderChatEmpty={() => (
                      <View style={styles.emptyListContainer}>
                        <Text style={styles.errorMessage}>
                          {t('noPinnedMessages')}
                        </Text>
                      </View>
                    )}
                  />
                </View>
              </MenuProvider>
            </SafeAreaView>
          </SafeAreaProvider>
        </Modal>
      ) : null}

      <ScrollToUnreadButton
        visible={showScrollButton}
        onPress={scrollToUnreadMessage}
        onDismiss={handleDismissScrollToUnread}
        count={unreadMessageCount}
        isLoading={isScrollingToUnread}
      />

      {user ? (
        <GiftedChat
          messagesContainerRef={ref as React.RefObject<AnimatedList<IMessage>>}
          listProps={giftedChatListViewProps}
          keyboardAvoidingViewProps={{ keyboardVerticalOffset }}
          messages={chatMessages}
          loadEarlierMessagesProps={{
            isAvailable: !!hasMoreMessages,
            isLoading: !!isLoadingEarlier,
            onPress: onLoadEarlier,
          }}
          user={{
            _id: user.id,
            name: `${user.firstName} ${user.lastName}`,
            avatar: user.photoUrl ?? undefined,
          }}
          onSend={onPressSendMessage}
          maxComposerHeight={150}
          minComposerHeight={Platform.OS === 'web' ? 300 : undefined}
          renderInputToolbar={
            readOnly
              ? () => <View />
              : online
              ? props => <CustomInputToolbar props={props} />
              : () => <Text style={styles.offlineText}>Offline</Text>
          }
          renderComposer={props => (
            <CustomComposer
              props={props}
              chatGroupId={conversation.id}
              messageBody={messageBody}
              selectedAttachments={selectedAttachments}
              setChatMessageBody={setChatMessageBody}
              pasteAttachments={pasteAttachments}
              disabled={isUploading}
              replyMessage={replyMessage}
              setReplyMessage={setReplyMessage}
              shouldFocusInput={shouldFocusInput}
              setShouldFocusInput={setShouldFocusInput}
            />
          )}
          renderSend={props => (
            <CustomSendButton
              props={props}
              shouldShowSend={
                selectedAttachments.length > 0 || messageBody.length > 0
              }
              disabled={isSendDisabled}
            />
          )}
          minInputToolbarHeight={readOnly ? 0 : undefined}
          text={prefillMessageText}
          textInputProps={{
            onChangeText: handleInputTextChanged,
            placeholder: t('typeAMessage'),
          }}
          onLongPressMessage={onChatMessageLongPress}
          renderBubble={renderBubble}
          renderMessage={renderMessage}
          renderTime={renderTime}
          renderDay={renderDay}
          isDayAnimationEnabled={false}
          isCustomViewBottom={false}
          renderUsername={() => null}
          renderAccessory={
            canPostFile ? () => renderAccessoryButtons() : undefined
          }
          renderMessageImage={renderMessageImage}
          renderAvatar={renderAvatar}
          renderChatEmpty={() => (
            <View
              style={[
                styles.emptyListContainer,
                {
                  paddingBottom:
                    (dimensions.height -
                      (headerHeight +
                        MESSAGE_ACCESSORY_BAR_HEIGHT +
                        COMPOSER_HEIGHT)) /
                    2,
                },
              ]}
            >
              <Text style={styles.errorMessage}>{t('noMessages')}</Text>
            </View>
          )}
          messagesContainerStyle={{
            paddingBottom: messagesBottomPadding,
            flexGrow: 1,
          }}
        />
      ) : (
        <ActivityIndicator
          style={styles.messageLoadingIndicator}
          size="large"
          color="black"
        />
      )}
    </DragDropZone>
  )
}

interface MessageBubbleProps {
  conversation: Conversation
  message: Message | undefined
  messageGroup: Message[] | undefined
  bubbleProps: BubbleProps<ChatMessage>
  isPreviousBubbleHasReactions: boolean
  readByCount?: number
  members: readonly Member[] | undefined | null
  onShowReactors(messageId: string, reaction: Emoji): void
  onReactMessageAction(messageId: String, emoji: Emoji): void
  onBubblePress(attachment: Attachment): void
  userColor: string
  onLongPress(context: any, message?: Message): void
  onLinkPress(url: string): void
  showNames: boolean
  replyMessage?: Message
}

export function MessageBubble({
  conversation,
  message,
  messageGroup,
  bubbleProps,
  isPreviousBubbleHasReactions,
  readByCount,
  members,
  onShowReactors,
  onReactMessageAction,
  userColor,
  showNames,
  onLongPress,
  onLinkPress,
  replyMessage,
}: MessageBubbleProps) {
  const styles = useStyles()
  const dimensions = useWindowDimensions()
  if (!messageGroup || messageGroup.length === 0) {
    return null
  }

  const conversationType = conversation.type
  const isDm = conversationType === 'dm' ? true : false
  const { reactions, mentions } = getMessageGroupData(messageGroup)

  const firstMessage = messageGroup[0]
  const isDeleted = firstMessage.isDeleted
  const isSender = bubbleProps.position === 'right'
  const isReadByAll = readByCount === (members?.length ?? 0) - 1
  const isCurrentMessageGroupHasReactions = reactions.length > 0
  const { attachments } = getMessageGroupData(messageGroup)

  if (Platform.OS === 'web') {
    const width =
      WEB_SCREEN_WIDTH < dimensions.width
        ? WEB_SCREEN_WIDTH - 100
        : dimensions.width - 100
    bubbleProps['wrapperStyle'] = {
      left: {
        maxWidth: width,
      },
      right: {
        maxWidth: width,
      },
    }
  }

  const containerToPreviousStyle: LeftRightStyle<ViewStyle> = {
    right: { borderTopRightRadius: 15 },
    left: { borderTopLeftRadius: 15 },
  }

  const containerToNextStyle: LeftRightStyle<ViewStyle> = {
    right: { borderBottomRightRadius: 15 },
    left: { borderBottomLeftRadius: 15 },
  }

  const renderTicks = () => {
    if (!isSender || isDeleted) return null

    const getIconName = () => {
      if (isReadByAll) return 'checkmark-done'
      if (!isDm && isNil(readByCount)) return 'checkmark-outline'
      return 'checkmark-done'
    }

    return (
      <Icon
        name={getIconName()}
        type="ionicon"
        size={16}
        style={{ paddingRight: 5 }}
        color={
          isReadByAll
            ? styles.readByAllTickColor.color
            : styles.unreadTickColor.color
        }
      />
    )
  }

  return (
    <View
      onContextMenu={event => {
        if (bubbleProps.onLongPressMessage) {
          event.preventDefault()
          bubbleProps.onLongPressMessage(null, bubbleProps.currentMessage)
        }
      }}
    >
      <Bubble
        {...bubbleProps}
        textStyle={{
          right: [bubbleProps.textStyle?.right, styles.selfBubbleText],
          left: [bubbleProps.textStyle?.left, styles.otherBubbleText],
        }}
        containerToPreviousStyle={
          isPreviousBubbleHasReactions ? containerToPreviousStyle : {}
        }
        containerToNextStyle={
          isCurrentMessageGroupHasReactions ? containerToNextStyle : {}
        }
        wrapperStyle={{
          left: [bubbleProps.wrapperStyle?.left, styles.otherBubbleContainer],
          right: [bubbleProps.wrapperStyle?.right, styles.selfBubbleContainer],
        }}
        isCustomViewBottom={false}
        renderCustomView={() => {
          return isSender || !showNames ? null : (
            <View>
              <Text
                style={[{ color: userColor }, styles.userName]}
                textBreakStrategy="simple"
              >
                {stripInvalidCodepoints(
                  bubbleProps.currentMessage?.user.name ?? ''
                )}
              </Text>
            </View>
          )
        }}
        renderTicks={renderTicks}
        renderMessageText={(props: MessageTextProps<ChatMessage>) => {
          return (
            <BubbleMessageText
              props={props}
              onLongPress={() => onLongPress(null, message)}
              onLinkPress={onLinkPress}
              isDeleted={message?.isDeleted}
              mentions={mentions}
              message={message}
              replyMessage={replyMessage}
              attachments={attachments}
            />
          )
        }}
      />
      {isCurrentMessageGroupHasReactions && !isDeleted ? (
        <Reactions
          position={bubbleProps?.position}
          message={firstMessage}
          reactions={reactions}
          onShowReactors={onShowReactors}
          onReactMessageAction={onReactMessageAction}
        />
      ) : null}
    </View>
  )
}

interface BubbleMessageImageProps {
  attachment: Attachment | undefined
  onAttachmentPress: (attachment: Attachment) => void
  onLongPressAttachment: (attachment: Attachment) => void
  size?: 'quarter' | 'half' | 'full' | 'default'
  count?: number
}

function BubbleMessageImage({
  attachment,
  onAttachmentPress,
  onLongPressAttachment,
  size = 'full',
  count,
}: BubbleMessageImageProps) {
  const styles = useStyles()
  if (attachment) {
    if (size === 'default') {
      return (
        <Pressable
          onPress={() => onAttachmentPress(attachment)}
          onLongPress={() => onLongPressAttachment(attachment)}
          style={{ justifyContent: 'center', alignItems: 'center' }}
        >
          {attachment.thumbnail ? (
            <ThumbnailImage
              key={attachment.uri}
              thumbnail={attachment.thumbnail}
            />
          ) : (
            <Image style={styles.emptyMessageImage} />
          )}
          {attachment.mimeType.startsWith('video') ? (
            <View
              style={[
                styles.thumbnailOverlay,
                attachment.thumbnail
                  ? {
                      aspectRatio:
                        attachment.thumbnail.width /
                        attachment.thumbnail.height,
                      width: THUMBNAIL_IMAGE_WIDTH,
                    }
                  : styles.videoIconOverlay,
              ]}
            >
              <Image
                style={styles.videoIcon}
                source={require('assets/chat/play.png')}
                contentFit="contain"
              />
            </View>
          ) : null}
        </Pressable>
      )
    }
    return (
      <Pressable
        disabled={!!count}
        onPress={() => onAttachmentPress(attachment)}
        onLongPress={() => onLongPressAttachment(attachment)}
        style={{ justifyContent: 'center', alignItems: 'center' }}
      >
        <PressableOpacity
          disabled={!count}
          onLongPress={() => onLongPressAttachment(attachment)}
          onPress={() => onAttachmentPress(attachment)}
        >
          {
            <Image
              key={attachment.uri}
              style={[
                styles.messageImage,
                size === 'full'
                  ? styles.fullImage
                  : size === 'half'
                  ? styles.halfImage
                  : styles.quarterImage,
              ]}
              source={attachment.thumbnail?.url}
              contentFit="cover"
            />
          }
          {attachment.mimeType.startsWith('video') ? (
            <View
              style={[
                styles.thumbnailOverlay,
                size === 'full'
                  ? styles.fullImage
                  : size === 'half'
                  ? styles.halfImage
                  : styles.quarterImage,
              ]}
            >
              <Image
                style={styles.videoIcon}
                source={require('assets/chat/play.png')}
                contentFit="contain"
              />
            </View>
          ) : null}
          {count ? (
            <View style={[styles.thumbnailOverlay, styles.quarterImage]}>
              <NonSelectableText
                style={styles.countText}
              >{`+ ${count}`}</NonSelectableText>
            </View>
          ) : null}
        </PressableOpacity>
      </Pressable>
    )
  }
  return null
}

function BubbleMessagesImagesContainer({
  children,
  showLabel = false,
  isMultipleImages = true,
  isUserSender,
}: {
  children: JSX.Element
  isUserSender: boolean
  showLabel?: boolean
  isMultipleImages?: boolean
}) {
  const styles = useStyles()
  return (
    <View style={styles.attachmentsContainer}>
      {showLabel ? (
        <NonSelectableText style={[styles.attachmentsLabel]}>
          {t('media')}
        </NonSelectableText>
      ) : null}
      {isMultipleImages ? (
        <View style={styles.multipleImagesContainer}>{children}</View>
      ) : (
        children
      )}
    </View>
  )
}

function BubbleMessagesDocumentsContainer({
  children,
  showLabel = false,
  isMultipleImages = true,
  isUserSender,
}: {
  children: JSX.Element
  isUserSender: boolean
  showLabel?: boolean
  isMultipleImages?: boolean
}) {
  const styles = useStyles()
  return (
    <View style={styles.attachmentsContainer}>
      {showLabel ? (
        <NonSelectableText style={[styles.attachmentsLabel]}>
          {t('documents')}
        </NonSelectableText>
      ) : null}
      {isMultipleImages ? (
        <View style={styles.multipleDocumentsContainer}>{children}</View>
      ) : (
        children
      )}
    </View>
  )
}

interface BubbleMessageImagesProps {
  attachments: Attachment[] | undefined
  onLongPressAttachment: (attachment: Attachment) => void
  onShowMultiAttachments: () => void
  onAttachmentPress: (attachment: Attachment) => void
  isUserSender: boolean
}

export function BubbleMessageImages({
  isUserSender,
  attachments,
  onAttachmentPress,
  onLongPressAttachment,
  onShowMultiAttachments,
}: BubbleMessageImagesProps) {
  const styles = useStyles()
  const mediaAttachments = attachments?.filter(
    attachment => !isPdfAttachment(attachment)
  )
  const documentAttachments = attachments?.filter(attachment =>
    isPdfAttachment(attachment)
  )

  let MediaAttachmentsComponent: JSX.Element = <></>
  let DocumentAttachmentsComponent: JSX.Element = <></>

  if (mediaAttachments?.length) {
    switch (mediaAttachments.length) {
      case 1:
        MediaAttachmentsComponent = (
          <BubbleMessageImage
            attachment={mediaAttachments[0]}
            onAttachmentPress={onAttachmentPress}
            onLongPressAttachment={onLongPressAttachment}
            size={documentAttachments?.length ? 'full' : 'default'}
            key={mediaAttachments[0].uri}
          />
        )
        break
      case 2:
        MediaAttachmentsComponent = (
          <>
            {mediaAttachments.map((attachment, index) => (
              <BubbleMessageImage
                attachment={attachment}
                onAttachmentPress={onAttachmentPress}
                onLongPressAttachment={onLongPressAttachment}
                size="half"
                key={`${attachment.uri}-${index}`}
              />
            ))}
          </>
        )
        break
      case 3:
        MediaAttachmentsComponent = (
          <>
            {mediaAttachments.map((attachment, index) => (
              <BubbleMessageImage
                attachment={attachment}
                onAttachmentPress={onAttachmentPress}
                onLongPressAttachment={onLongPressAttachment}
                size={index === 0 ? 'half' : 'quarter'}
                key={`${attachment.uri}-${index}`}
              />
            ))}
          </>
        )
        break
      case 4:
        MediaAttachmentsComponent = (
          <>
            {mediaAttachments.map((attachment, index) => (
              <BubbleMessageImage
                attachment={attachment}
                onAttachmentPress={onAttachmentPress}
                onLongPressAttachment={onLongPressAttachment}
                size="quarter"
                key={`${attachment.uri}-${index}`}
              />
            ))}
          </>
        )
        break
      default:
        MediaAttachmentsComponent = (
          <>
            {mediaAttachments.slice(0, 4).map((attachment, index) => (
              <BubbleMessageImage
                attachment={attachment}
                onAttachmentPress={
                  index === 3 ? onShowMultiAttachments : onAttachmentPress
                }
                onLongPressAttachment={
                  index === 3 ? () => null : onLongPressAttachment
                }
                size="quarter"
                key={`${attachment.uri}-${index}`}
                count={index === 3 ? mediaAttachments.length - 3 : undefined}
              />
            ))}
          </>
        )
    }
  }
  if (documentAttachments?.length) {
    DocumentAttachmentsComponent = (
      <>
        {documentAttachments.map((attachment, index) =>
          attachment.thumbnail ? (
            <View key={`${attachment.uri}-${index}`}>
              <BubbleMessageImage
                attachment={attachment}
                size="half"
                onLongPressAttachment={onLongPressAttachment}
                onAttachmentPress={onAttachmentPress}
              />
              <View style={styles.documentNameContainer}>
                <NonSelectableText numberOfLines={2} textBreakStrategy="simple">
                  {attachment.name?.length
                    ? stripInvalidCodepoints(attachment.name)
                    : 'PDF Document'}
                </NonSelectableText>
              </View>
            </View>
          ) : null
        )}
      </>
    )
  }

  return (
    <>
      <BubbleMessagesImagesContainer
        isUserSender={isUserSender}
        showLabel={!!mediaAttachments?.length && !!documentAttachments?.length}
        isMultipleImages={(mediaAttachments?.length ?? 0) > 1}
      >
        {MediaAttachmentsComponent}
      </BubbleMessagesImagesContainer>
      <BubbleMessagesDocumentsContainer
        isUserSender={isUserSender}
        showLabel={!!mediaAttachments?.length && !!documentAttachments?.length}
        isMultipleImages={true}
      >
        {DocumentAttachmentsComponent}
      </BubbleMessagesDocumentsContainer>
    </>
  )
}

export function BubbleMessageText({
  message,
  props,
  onLongPress,
  onLinkPress,
  isDeleted,
  attachments,
  mentions,
}: {
  props: MessageTextProps<ChatMessage>
  message?: Message
  onLongPress: () => void
  onLinkPress: (url: string) => void
  isDeleted?: boolean
  mentions?: Mention[]
  replyMessage?: Message
  attachments: Attachment[]
}) {
  const { currentMessage, position } = props
  const body = stripInvalidCodepoints(currentMessage?.text || '')
  const styles = useStyles()

  const mentionNames = mentions?.map(mention =>
    stripInvalidCodepoints(
      `@${mention.user.firstName} ${mention.user.lastName}`
    )
  )

  if (isDeleted) {
    return (
      <View style={styles.deletedMessageContainer}>
        <Icon
          name="trash"
          type="font-awesome-5"
          size={16}
          color={'grey'}
          style={{ marginRight: 5 }}
          solid
        />
        <NonSelectableText style={styles.deletedMessageText}>
          {body}
        </NonSelectableText>
      </View>
    )
  }

  const isSender = position === 'right'

  let url = ''
  if (body && body.length > 0) {
    url = extractUrl(body)
  }
  const isCurrentMessageHasAttachment = !!currentMessage?.image
  const isCurrentMessageHasLink = !!url

  const baseTextStyle = isSender
    ? props.textStyle?.right
    : props.textStyle?.left

  const mentionParseShapes = mentionNames?.length
    ? mentionNames.map(m => ({
        pattern: new RegExp(regExEscape(m), 'g'),
        style: styles.mentionText,
      }))
    : []

  const linkParseShape = {
    type: 'url',
    style: styles.linkText,
    onPress: onLinkPress,
  } as const

  // Filter mentions that don't already appear in the message body
  const missingMentions =
    mentionNames?.filter(mention => !body.includes(mention)) || []

  return (
    <Pressable
      onLongPress={onLongPress}
      style={
        isCurrentMessageHasLink && !isCurrentMessageHasAttachment
          ? { marginTop: 7 }
          : {}
      }
    >
      {attachments && attachments.length > 0 ? null : (
        <MessageReplyBubble
          isSender={false}
          replyMessage={message?.replyToMessage}
        />
      )}

      <LinkPreviewComponent
        url={url}
        onLongPress={onLongPress}
        isSender={isSender}
        onLinkPress={onLinkPress}
      />
      <View
        style={[
          styles.messageTextContainer,
          isCurrentMessageHasLink || isCurrentMessageHasAttachment
            ? { maxWidth: THUMBNAIL_IMAGE_WIDTH }
            : undefined,
        ]}
      >
        <ParsedText
          style={[baseTextStyle, { fontWeight: 'normal' }]}
          parse={[linkParseShape, ...mentionParseShapes]}
          childrenProps={{ selectable: false }}
          allowFontScaling={false}
          textBreakStrategy="simple"
        >
          {`${body}${
            missingMentions.length ? ' ' + missingMentions.join(' ') : ''
          }`}
        </ParsedText>
      </View>
    </Pressable>
  )
}

function UnreadMessageIndicator({ count }: { count: number | undefined }) {
  const styles = useStyles()
  return (
    <View style={styles.unreadMessagesRow}>
      <View style={styles.unreadMessagesContainer}>
        <Text style={styles.unreadMessagesText}>
          {t('unreadMessageCount', { count })}
        </Text>
      </View>
    </View>
  )
}

function ScrollToUnreadButton({
  onPress,
  onDismiss,
  visible,
  count,
  isLoading = false,
}: {
  onPress: () => void
  onDismiss: () => void
  visible: boolean
  count: number | undefined
  isLoading?: boolean
}) {
  const styles = useStyles()

  if (!visible) return null

  return (
    <Animated.View
      style={styles.scrollToUnreadButton}
      entering={FadeIn.duration(200)}
      exiting={FadeOut.duration(200)}
    >
      <PressableOpacity
        style={[
          styles.scrollToUnreadButtonInner,
          isLoading && { opacity: 0.7 },
        ]}
        onPress={onPress}
        disabled={isLoading}
      >
        {isLoading ? (
          <ActivityIndicator size="small" color="#FFFFFF" />
        ) : (
          <Icon
            name="arrow-up-circle"
            size={20}
            type="material-design"
            color="#FFFFFF"
          />
        )}
        <Text style={styles.scrollToUnreadButtonText}>
          {t('newMessagesCount', { count })}
        </Text>
        <PressableOpacity
          onPress={onDismiss}
          style={styles.scrollToUnreadButtonDismiss}
          disabled={isLoading}
        >
          <Icon
            name="close-circle"
            type="material-design"
            size={20}
            color="#FFFFFF"
          />
        </PressableOpacity>
      </PressableOpacity>
    </Animated.View>
  )
}

export const useStyles = makeStyles(theme => ({
  name: {
    color: theme.mode === 'dark' ? '#BBBBBB' : '#888888',
    fontSize: 12,
    paddingBottom: 5,
    paddingLeft: 10,
  },
  messageImage: {
    width: 200,
    height: 200,
    marginTop: 7,
    borderRadius: 8,
  },
  videoIcon: {
    height: 30,
    width: 30,
  },
  multipleImagesContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    width: THUMBNAIL_IMAGE_WIDTH + 2 * THUMBNAIL_IMAGE_MARGIN_HORIZONTAL,
    justifyContent: 'space-evenly',
    alignItems: 'center',
  },
  multipleDocumentsContainer: {
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 3,
    paddingHorizontal: 5,
  },
  thumbnailOverlay: {
    position: 'absolute',
    zIndex: 999,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    marginTop: 7,
  },
  videoIconOverlay: {
    width: 200,
    height: 200,
  },
  emptyMessageImage: {
    width: 200,
    height: 200,
    borderRadius: 8,
    marginHorizontal: 5,
    marginTop: 10,
  },
  countOverlay: {
    width: 115,
    height: 115,
    marginTop: 7,
  },
  fullImage: {
    width: THUMBNAIL_IMAGE_WIDTH,
    height: THUMBNAIL_IMAGE_WIDTH,
  },
  halfImage: {
    width: THUMBNAIL_IMAGE_WIDTH,
    height: THUMBNAIL_IMAGE_WIDTH / 2,
  },
  quarterImage: {
    width: THUMBNAIL_IMAGE_WIDTH / 2,
    height: THUMBNAIL_IMAGE_WIDTH / 2,
  },
  countText: {
    fontSize: 30,
    color: 'white',
    fontWeight: 'bold',
  },
  documentNameContainer: {
    position: 'absolute',
    bottom: 0,
    backgroundColor:
      theme.mode === 'dark' ? 'rgba(0,0,0,0.5)' : 'rgba(255,255,255,0.7)',
    width: '100%',
    borderBottomStartRadius: 8,
    borderBottomEndRadius: 8,
    padding: 5,
  },
  attachmentsLabel: {
    marginLeft: 10,
    marginTop: 10,
    color: theme.colors.grey9,
    textTransform: 'uppercase',
  },
  attachmentsContainer: { justifyContent: 'center' },
  selfBubbleContainer: {
    backgroundColor: theme.mode === 'dark' ? '#144D37' : '#D0FECF',
  },
  otherBubbleContainer: {
    backgroundColor: theme.mode === 'dark' ? '#232626' : 'white',
  },
  selfTextStyle: {
    fontWeight: 'normal',
    color: theme.mode === 'dark' ? '#FDFDFD' : 'black',
  },
  otherTextStyle: {
    fontWeight: 'normal',
    color: theme.mode === 'dark' ? '#FDFDFD' : 'black',
  },
  linkText: {
    color: theme.mode === 'dark' ? '#27BE64' : '#155F3C',
  },
  mentionText: {
    color: theme.mode === 'dark' ? '#27BE64' : '#1E8555',
    fontWeight: 'bold',
  },
  container: {
    flex: 1,
    backgroundColor: theme.mode === 'dark' ? '#111111' : '#F5EDE0',
    justifyContent: 'center',
  },
  descriptionBox: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#FFFFB7',
  },
  descriptionText: {
    flex: 1,
    color: '#4E5665',
    paddingTop: 10,
    paddingStart: 15,
    paddingBottom: 10,
    fontSize: 13,
  },
  descriptionCloseButton: {
    width: 25,
    height: 25,
    paddingTop: 5,
    textAlign: 'center',
    fontSize: 15,
    fontFamily: font.bold,
    color: theme.colors?.grey3,
  },
  errorMessage: {
    fontSize: 17,
    textAlign: 'center',
  },
  messageLoadingIndicator: {
    flex: 1,
  },
  offlineText: {
    padding: 10,
    fontSize: 15,
    fontStyle: 'italic',
    textAlign: 'center',
    textAlignVertical: 'center',
  },
  pinnedMessagesHeader: {
    height: 50,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: 15,
    padding: 10,
    paddingRight: 20,
    backgroundColor: theme.mode === 'dark' ? '#121212' : '#f9f9f9',
    borderBottomColor: 'blue',
    borderBottomWidth: 0,
  },
  pinnedMessagesHeaderText: {
    flex: 1,
    color: theme.colors?.primary,
    fontSize: 20,
    fontFamily: font.bold,
    marginLeft: 5,
  },
  pinnedChatMessages: {
    zIndex: 10,
    backgroundColor:
      theme.mode === 'dark' ? theme.colors?.grey6 : theme.colors.teal,
  },
  emptyListContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    transform: Platform.OS === 'android' ? [{ scale: -1 }] : [{ scaleY: -1 }],
  },
  pinnedButtonContainer: { zIndex: 10, flexDirection: 'row' },
  pinnedMessagesModal: {
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
    backgroundColor:
      theme.mode === 'dark' ? theme.colors?.grey6 : theme.colors.teal,
  },
  pinnedMessagesModalContainer: {
    flex: 1,
    backgroundColor: 'transparent',
    ...(Platform.OS === 'web' && { maxWidth: WEB_SCREEN_WIDTH }),
  },
  emptyFooter: { height: 70 },
  selfTimeText: { color: theme.mode === 'dark' ? '#84A296' : '#698168' },
  otherTimeText: {
    color: theme.mode === 'dark' ? theme.colors.grey1 : theme.colors?.grey9,
  },
  dayText: {
    color: theme.mode === 'dark' ? '#F9F9F9' : '#090909',
  },
  deletedMessageContainer: {
    flexDirection: 'row',
    marginTop: 5,
    marginBottom: 5,
    marginLeft: 10,
    marginRight: 10,
    alignItems: 'center',
    ...(Platform.OS === 'web'
      ? { maxWidth: WEB_SCREEN_WIDTH }
      : { maxWidth: '95%' }),
  },
  deletedMessageText: {
    color: 'grey',
    fontSize: 16,
    lineHeight: 20,
    flexShrink: 1,
    fontStyle: 'italic',
  },
  readByAllTickColor: {
    color: '#53bdeb',
  },
  unreadTickColor: {
    color: theme.mode === 'dark' ? '#89A699' : '#678066',
  },
  mentionsInlineContainer: {
    marginTop: 4,
    flexDirection: 'row',
  },
  messageTextContainer: {
    marginHorizontal: 10,
    marginVertical: 5,
  },
  userName: {
    paddingHorizontal: 10,
    paddingTop: 5,
    fontWeight: 'bold',
    fontSize: 16,
  },
  selfBubbleText: {
    color: theme.mode === 'dark' ? '#fdfdfd' : '#0A0A0A',
    fontSize: 16,
  },
  otherBubbleText: {
    color: theme.mode === 'dark' ? '#fdfdfd' : '#0A0A0A',
    fontSize: 16,
  },
  replyIconContainer: {
    width: 30,
    alignItems: 'center',
    justifyContent: 'center',
    alignContent: 'center',
    marginLeft: 10,
  },
  unreadMessagesRow: {
    flex: 1,
    backgroundColor:
      theme.mode === 'dark' ? getDarkShadeColor() : getLightShadeColor(0.5),
    justifyContent: 'center',
    alignItems: 'center',
    marginVertical: 5,
  },
  unreadMessagesContainer: {
    paddingVertical: 5,
    paddingHorizontal: 30,
    backgroundColor: theme.mode === 'dark' ? '#232626' : theme.colors.white,
    marginVertical: 5,
    borderRadius: 20,
  },
  unreadMessagesText: {
    color: theme.colors.black,
    fontWeight: 'bold',
  },
  scrollToUnreadButton: {
    position: 'absolute',
    top: 30,
    alignSelf: 'center',
    zIndex: 1000,
  },
  scrollToUnreadButtonInner: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors?.blue,
    padding: 6,
    borderRadius: 25,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  scrollToUnreadButtonText: {
    color: '#FFFFFF',
    marginLeft: 8,
    fontSize: 14,
  },
  scrollToUnreadButtonDismiss: {
    marginLeft: 4,
  },
}))

function regExEscape(s: string) {
  return s.replace(/[$-\/?[-^{|}]/g, '\\$&')
}

function getMessagesBottomPadding(
  hasAttachments: boolean,
  hasReplyMessage: boolean
) {
  if (hasAttachments && hasReplyMessage) {
    return ATTACHMENT_PREVIEW_SIZE + COMPOSER_HEIGHT + REPLY_MESSAGE_BAR_HEIGHT
  } else if (hasAttachments) {
    return ATTACHMENT_PREVIEW_SIZE + COMPOSER_HEIGHT
  } else if (hasReplyMessage) {
    return COMPOSER_HEIGHT + REPLY_MESSAGE_BAR_HEIGHT
  } else {
    return 0
  }
}
